/* global google */
import React, { useRef, useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import {
    GOOGLE_MAPS_API_KEY,
    GOOGLE_MAPS_LOADER_LIBRARIES,
    GOOGLE_MAPS_LOADER_SCRIPT_ID,
} from '../config/api';
import { clsx } from 'clsx';

export interface AddressPlaceResolved {
    formattedAddress: string;
    placeId?: string;
}

interface AddressAutocompleteProps {
    value?: string;
    onChange: (value: string) => void;
    /** Fires when the user picks a place from Google suggestions (includes optional place_id). Called after onChange. */
    onPlaceResolved?: (place: AddressPlaceResolved) => void;
    label?: string;
    error?: string;
    placeholder?: string;
    className?: string;
}

export default function AddressAutocomplete({
    value,
    onChange,
    onPlaceResolved,
    label,
    error,
    placeholder = "Enter address",
    className,
}: AddressAutocompleteProps) {
    const { isLoaded } = useJsApiLoader({
        id: GOOGLE_MAPS_LOADER_SCRIPT_ID,
        googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
        libraries: [...GOOGLE_MAPS_LOADER_LIBRARIES],
    });

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const [inputValue, setInputValue] = useState(value || '');

    const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    const onPlaceChanged = () => {
        if (autocompleteRef.current !== null) {
            const place = autocompleteRef.current.getPlace();
            if (place.formatted_address) {
                setInputValue(place.formatted_address);
                onChange(place.formatted_address);
                onPlaceResolved?.({
                    formattedAddress: place.formatted_address,
                    placeId: place.place_id,
                });
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        onChange(e.target.value);
    };

    // Synchronize internal state with external value if it changes
    React.useEffect(() => {
        if (value !== undefined) {
            setInputValue(value);
        }
    }, [value]);

    const inputId = React.useId();

    if (!isLoaded) {
        return (
            <div className="space-y-1">
                {label && (
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}
                    </label>
                )}
                <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-10 w-full rounded-md" />
            </div>
        );
    }

    return (
        <div className="space-y-1">
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {label}
                </label>
            )}
            <Autocomplete
                onLoad={onLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                    fields: ['formatted_address', 'geometry', 'name', 'place_id'],
                }}
            >
                <input
                    id={inputId}
                    type="text"
                    className={clsx(
                        'input w-full',
                        error && 'border-red-500 focus-visible:ring-red-500',
                        className
                    )}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    autoComplete="off"
                />
            </Autocomplete>
            {error && (
                <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
        </div>
    );
}
